import type {components} from '@/lib/api-schema'
import {ORDEN_SECCIONES, type SeccionClave} from '@/lib/bateria-cola'

type AlcanceCola = components['schemas']['AlcanceCola']

/** Agrupar los cuatro estados en tres lo decide el backend (`bateria_cola.FiltroEstado`). */
export type EstadoFiltro = components['schemas']['FiltroEstado']

export const ESTADOS_FILTRO: EstadoFiltro[] = ['sin_confirmar', 'pendientes', 'decididos']

/** `dejadas` es un marcador personal que el backend no conoce: viaja como `alcance=todo` + refs. */
export type RevisarCola = 'pendientes' | 'dejadas' | 'todo'

const REVISAR: RevisarCola[] = ['pendientes', 'dejadas', 'todo']

/** Un filtro solo reduce lo que la sesión camina: nunca redefine la cola, su cursor ni sus tramos. */
export type FiltrosCola = {
  /** Vacío = todo el lote. */
  paqueteIds: string[]
  revisar: RevisarCola
  /** `0 < x ≤ 1`, o `null` con el filtro apagado. */
  confianzaMax: number | null
  /** La petición dirigida que llegó por la URL (`ref` separadas por coma), o `null`. */
  solo: string | null
  /** Vacío = los cuatro estados; bajo `revisar=pendientes` no cambia nada. */
  estados: EstadoFiltro[]
  /** Solo posiciona el cursor al montar: no entra en la firma ni se re-emite. */
  irA: SeccionClave | null
  /** `?filtros=1`: abre el panel al aterrizar. Bandera de UI pura, con el mismo trato que `irA`. */
  abrirPanel: boolean
}

/** La forma del GET: `revisar=dejadas` llega traducido a `alcance=todo` + `solo`. */
export type FiltroCola = {
  paqueteIds?: string[]
  solo?: string
  alcance?: AlcanceCola
  estados?: EstadoFiltro[]
  confianzaMax?: number | null
}

/** uvicorn/h11 corta la línea de petición en ~8 KB y una ref de marca mide ~65 bytes con su coma:
 * 100 son ~6,5 KB. Pasado el tope no se recorta en silencio: la pantalla lo dice. */
export const MAX_REFS_EN_URL = 100

function confianzaDe(crudo: string | null): number | null {
  if (crudo === null) return null
  const valor = Number(crudo)
  return Number.isFinite(valor) && valor > 0 && valor <= 1 ? valor : null
}

/** `alcance=todo` se sigue leyendo porque hay enlaces vivos con esa grafía. Un valor que no calza cae
 * al default: una URL escrita a mano abre la cola completa, nunca una pantalla en blanco. */
export function leerFiltros(params: URLSearchParams): FiltrosCola {
  const pedido = params.get('revisar')
  const revisar =
    REVISAR.find((valor) => valor === pedido) ?? (params.get('alcance') === 'todo' ? 'todo' : 'pendientes')
  const solo = params.get('solo')
  return {
    paqueteIds: params.getAll('paquete_id'),
    revisar,
    estados: params
      .getAll('estado')
      .filter((valor): valor is EstadoFiltro => ESTADOS_FILTRO.some((conocido) => conocido === valor)),
    confianzaMax: confianzaDe(params.get('confianza_max')),
    solo: solo ? solo : null,
    irA: ORDEN_SECCIONES.find((clave) => clave === params.get('ir_a')) ?? null,
    abrirPanel: params.get('filtros') === '1',
  }
}

/** Lo que está en su default no se escribe. `ir_a` y `filtros` nunca se re-emiten: la pantalla se
 * re-posicionaría sola en cada cambio de filtro. */
export function urlDeFiltros(filtros: FiltrosCola): string {
  const params = new URLSearchParams()
  for (const id of filtros.paqueteIds) params.append('paquete_id', id)
  if (filtros.revisar !== 'pendientes') params.set('revisar', filtros.revisar)
  for (const estado of filtros.estados) params.append('estado', estado)
  if (filtros.confianzaMax !== null) params.set('confianza_max', String(filtros.confianzaMax))
  if (filtros.solo !== null) params.set('solo', filtros.solo)
  const texto = params.toString()
  return texto === '' ? '' : `?${texto}`
}

/** Va en el `key` del subárbol de trabajo: al cambiar, React lo remonta y el cursor nace de cero,
 * porque el arranque de `useCursorCola` corta con `seccion !== null`. */
export function firmaDeFiltros(filtros: FiltrosCola): string {
  return [
    filtros.paqueteIds.join(','),
    filtros.revisar,
    filtros.confianzaMax ?? '',
    filtros.solo ?? '',
    filtros.estados.join(','),
  ].join('|')
}

/** Las más antiguas, que es el orden en que el almacén las guardó. */
export function refsParaPedir(refs: string[]): string[] {
  return refs.slice(0, MAX_REFS_EN_URL)
}

export function sinFiltrosActivos(filtros: FiltrosCola): boolean {
  return (
    filtros.paqueteIds.length === 0 &&
    filtros.solo === null &&
    filtros.revisar === 'pendientes' &&
    filtros.confianzaMax === null &&
    filtros.estados.length === 0
  )
}

/** Distingue una revisión terminada de un filtro que dejó la cola en cero. `colaCargada` porque
 * antes del primer tramo `totalFiltrado` vale 0 y pintaría el vacío en cada arranque. */
export function filtroDejoLaColaEnCero(filtros: FiltrosCola, colaCargada: boolean, totalFiltrado: number): boolean {
  return !sinFiltrosActivos(filtros) && colaCargada && totalFiltrado === 0
}

export function filtrosTrasVaciarFiltro(filtros: FiltrosCola): FiltrosCola {
  return filtros.confianzaMax !== null
    ? {...filtros, confianzaMax: null}
    : {...filtros, paqueteIds: [], revisar: 'pendientes', confianzaMax: null, solo: null, estados: []}
}

/** `dejadas` viaja como `todo` + `solo` porque `todo ⊇ dudas`: una dejada que ya no es duda igual
 * trae tarjeta. Sin refs, `solo` viaja vacío a propósito: ausente abriría el lote entero. */
export function pedidoDeCola(filtros: FiltrosCola, refsDejadas: string[]): FiltroCola {
  const solo = filtros.revisar === 'dejadas' ? refsParaPedir(refsDejadas).join(',') : (filtros.solo ?? undefined)
  return {
    paqueteIds: filtros.paqueteIds.length > 0 ? filtros.paqueteIds : undefined,
    solo,
    alcance: filtros.revisar === 'pendientes' ? undefined : 'todo',
    estados: filtros.estados.length > 0 ? filtros.estados : undefined,
    confianzaMax: filtros.confianzaMax,
  }
}
